/** @jsxImportSource react */
import { Autocomplete, TextField, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { membersAPI } from '../../api/crm'
import { useTenantKey } from '../../hooks/useCrmQueries'

export const ME = '$me'

/** Workspace members (shared cache with the Members page). */
export const useMembers = () => {
  const tenantId = useTenantKey()
  return useQuery({ queryKey: [tenantId, 'members'], queryFn: membersAPI.list, staleTime: 60_000 })
}

/** Members a person field can be set to: active and already signed in once (have a user id). */
export const assignable = (members = []) => members.filter((m) => m.active && m.userId)

/** Display name for a person value: "Me", the member's name (else email), or the record's resolved ref. */
export const usePersonLabel = () => {
  const { data: members = [] } = useMembers()
  const myEmail = useSelector((state) => state.auth.user?.email?.toLowerCase())
  return (value, ref) => {
    if (!value) return ''
    if (value === ME) return 'Me'
    const member = members.find((m) => m.userId === value)
    const label = member?.name || member?.email || ref?.title || 'Unknown member'
    const email = member?.email || ref?.detail || ''
    return email.toLowerCase() === myEmail ? `${label} (me)` : label
  }
}

/**
 * Name for an email the API reports as the actor (created by, updated by, uploaded by…): the
 * member's name when they've set one, else the email itself. Automations and unknown values pass through.
 */
export const useNameOf = () => {
  const { data: members = [] } = useMembers()
  const byEmail = Object.fromEntries(members.filter((m) => m.name).map((m) => [m.email.toLowerCase(), m.name]))
  return (email) => (email && byEmail[email.toLowerCase()]) || email
}

/** Picker for a USER field. Value is a member's user id, or "$me" (resolved on save). */
const PersonInput = ({ value, onChange, initialRef, label, required, error, helperText, disabled, allowMe = true }) => {
  const { data: members = [], isLoading } = useMembers()
  const labelOf = usePersonLabel()
  const options = [...(allowMe ? [ME] : []), ...assignable(members).map((m) => m.userId)]
  // keep an ex-member's current value visible even though it can't be chosen again
  if (value && !options.includes(value)) options.push(value)

  return (
    <Autocomplete
      disabled={disabled}
      loading={isLoading}
      options={options}
      value={value || null}
      getOptionLabel={(v) => labelOf(v, initialRef?.id === v ? initialRef : null)}
      renderOption={(props, v) => {
        const member = members.find((m) => m.userId === v)
        return (
          <li {...props} key={v}>
            <span>
              {labelOf(v, initialRef?.id === v ? initialRef : null)}
              {member?.name && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{member.email}</Typography>}
            </span>
          </li>
        )
      }}
      onChange={(_, next) => onChange(next || '')}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} error={!!error} helperText={error || helperText} placeholder="Choose a member" />
      )}
    />
  )
}

export default PersonInput
